import { Layer, Source, useMap } from "@vis.gl/react-maplibre";
import type { MapLayerMouseEvent } from "@vis.gl/react-maplibre";
import { useEffect } from "react";
import { SCORE_HEX } from "../../lib/mapConfig";
import { scoreLevel } from "../../lib/utils";
import type { EnrichedSpot } from "../../lib/types";

const LAYER_ID = "spots-circles";

interface SpotCirclesProps {
  spots: EnrichedSpot[];
  onSpotClick: (spotId: string) => void;
}

function buildGeoJSON(spots: EnrichedSpot[]): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: spots.map((spot) => ({
      type: "Feature",
      id: spot.id,
      geometry: {
        type: "Point",
        coordinates: [spot.coords.lon, spot.coords.lat],
      },
      properties: {
        id: spot.id,
        name: spot.name,
        color: SCORE_HEX[scoreLevel(spot.score)],
      },
    })),
  };
}

export function SpotCircles({ spots, onSpotClick }: SpotCirclesProps) {
  const { current: map } = useMap();

  // Wire click + cursor handlers onto the layer after map is ready.
  useEffect(() => {
    if (!map) return;

    const onClick = (e: MapLayerMouseEvent) => {
      const feature = e.features?.[0];
      const id = feature?.properties?.id as string | undefined;
      if (id) onSpotClick(id);
    };

    const onEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const onLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    map.on("click", LAYER_ID, onClick);
    map.on("mouseenter", LAYER_ID, onEnter);
    map.on("mouseleave", LAYER_ID, onLeave);

    return () => {
      map.off("click", LAYER_ID, onClick);
      map.off("mouseenter", LAYER_ID, onEnter);
      map.off("mouseleave", LAYER_ID, onLeave);
    };
  }, [map, onSpotClick]);

  const geojson = buildGeoJSON(spots);

  return (
    <Source id="spots-source" type="geojson" data={geojson}>
      {/* Glow / halo underneath */}
      <Layer
        id="spots-halo"
        type="circle"
        paint={{
          "circle-radius": 11,
          "circle-color": ["get", "color"],
          "circle-opacity": 0.15,
          "circle-blur": 1,
        }}
      />
      {/* Main circle */}
      <Layer
        id={LAYER_ID}
        type="circle"
        paint={{
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            4, 5,
            8, 8,
            12, 10,
          ],
          "circle-color": ["get", "color"],
          "circle-opacity": 0.9,
          "circle-stroke-width": 1.5,
          "circle-stroke-color": "rgba(0,0,0,0.5)",
        }}
      />
    </Source>
  );
}
